import Big from 'big.js';
import {
  DEFAULT_PRECISION,
  FULL_PRECISION,
  SMALL_PRECISION,
} from '@make-software/cspr-design';
import {
  ContractTypeId,
  Deploy,
  GetDeployResult,
  isContractTypeCep18,
} from '@block-explorer/entities';
import { DataResponse } from '../services';
import { getExecutionResultsFromDeployRawData } from '../services/hooks/use-get-execution-results';
import { isODRAWasmProxyTransaction, isTransferDeploy } from './contract.tsx';

// NOTE: Always use string as type for any fiat currency or token value

export const MOTES_PER_CSPR_RATE = '1000000000'; // 1 000 000 000 MOTES === 1 CSPR
export const CSPR_DECIMALS = 9;

export const motesToCSPR = (motes: string): string => {
  return Big(motes).div(MOTES_PER_CSPR_RATE).toString();
};

export const CSPRtoMotes = (cspr: string): string => {
  return Big(cspr).mul(MOTES_PER_CSPR_RATE).toString();
};

export const motesToCurrency = (
  motes: string,
  currencyPerCsprRate: number
): string => {
  if (currencyPerCsprRate === 0) {
    throw new Error('motesToCurrency: the CSPR rate cannot be zero');
  }

  return Big(motes)
    .div(MOTES_PER_CSPR_RATE)
    .mul(currencyPerCsprRate)
    .toString();
};

export enum PrecisionCase {
  'none' = 'none',
  'small' = 'small',
  'deployCost' = 'deployCost',
  'full' = 'full',
}

export const currencyPrecisionByCase = (precisionCase?: PrecisionCase) => {
  switch (precisionCase) {
    case PrecisionCase.small:
      return SMALL_PRECISION;

    case PrecisionCase.deployCost:
      return DEFAULT_PRECISION;

    case PrecisionCase.full:
      return FULL_PRECISION;

    case PrecisionCase.none:
      return 0;

    default:
      return 0;
  }
};

// @todo Why the contract related section is in currency.tsx
export const WASM_DEPLOY = 1;
export const WASM_PROXY_DEPLOY = 7;
export const NATIVE = 6;

export const isDeployAmountInCspr = (
  deploy: Deploy,
  deployRawData: DataResponse<GetDeployResult> | null = null
): boolean => {
  // If is transfer
  if (isTransferDeploy(deploy.contractHash, deploy?.entryPoint?.name)) {
    return true;
  }

  // If is system contract
  if (
    deploy.contractPackage?.contract_type_id === ContractTypeId.System ||
    deploy.contractPackage?.latest_version_contract_type_id ===
      ContractTypeId.System
  ) {
    return true;
  }

  // ODRA WasmProxy transaction
  if (isODRAWasmProxyTransaction(deploy)) {
    return true;
  }

  // If is CEP-18 contract
  if (
    isContractTypeCep18(deploy.contractPackage) ||
    Boolean(deploy?.ftActions?.length)
  ) {
    return false;
  }

  // if CSPR fun contract
  if (
    deploy.args?.csprfun_contract_hash_key &&
    deploy.args?.token_to_trade_contract_hash_key
  ) {
    return true;
  }

  const executionResult = getExecutionResultsFromDeployRawData(
    deployRawData?.data
  );
  // If it has raw deploy data and has transfers
  if (executionResult) {
    const transforms = executionResult?.effects || [];

    for (const transform of transforms) {
      if (
        transform?.kind?.isWriteTransfer() ||
        transform?.kind?.isWriteWithdraw() ||
        transform?.kind?.isWriteBid() ||
        transform?.kind?.isWriteUnbonding()
      ) {
        return true;
      }
    }
  }

  // If it has related transfers attached
  if (deploy?.transfers && deploy.transfers.length > 0) {
    if (!deploy?.args.amount || !deploy?.transfers) {
      return false;
    }

    const deployAmount = deploy?.args.amount?.parsed as string;
    const transfersAmount = deploy?.transfers.find((transfer) =>
      transfer.hasOwnProperty('amount')
    )?.amount as string;

    return Big(deployAmount).eq(Big(transfersAmount));
  }

  const deployAmount = deploy.amount || (deploy.args.amount?.parsed as string);
  // Assume, that amount, which less that minimum transfer are not in cspr
  const MIN_CSPR_AMOUNT_MOTES = Big(MOTES_PER_CSPR_RATE).mul(Big('2.5'));
  if (deployAmount && Big(deployAmount).lt(MIN_CSPR_AMOUNT_MOTES)) {
    return false;
  }

  // If amount is > 677,000,000 CSPR
  const MAX_CSPR_AMOUNT = '677000000';
  const MAX_MOTES_AMOUNT = Big(MAX_CSPR_AMOUNT).mul(MOTES_PER_CSPR_RATE);

  return deployAmount ? Big(deployAmount).lt(MAX_MOTES_AMOUNT) : false;
};

export const CURRENCIES = {
  USD: 'US Dollar',
  AUD: 'Australian Dollar',
  BRL: 'Brazilian Real',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CLP: 'Chilean Peso',
  CNY: 'Chinese Yuan',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  EUR: 'Euro',
  GBP: 'Pound Sterling',
  HKD: 'Hong Kong Dollar',
  HUF: 'Hungarian Forint',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli New Shekel',
  INR: 'Indian Rupee',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  MXN: 'Mexican Peso',
  MYR: 'Malaysian Ringgit',
  NOK: 'Norwegian Krone',
  NZD: 'New Zealand Dollar',
  PHP: 'Philippine Peso',
  PKR: 'Pakistani Rupee',
  PLN: 'Polish Złoty',
  RUB: 'Russian Ruble',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  TRY: 'Turkish Lira',
  TWD: 'New Taiwan Dollar',
  ZAR: 'South African Rand',
  VND: 'Vietnamese Dong',
  MAD: 'Moroccan Dirham',
  IRR: 'Iranian Rial',
  ARS: 'Argentine Peso',
  RON: 'Romanian Leu',
  UAH: 'Ukrainian Hryvnia',
  NGN: 'Nigerian Naira',
  AED: 'UAE Dirham',
  COP: 'Colombian Peso',
  EGP: 'Egyptian Pound',
  SAR: 'Saudi Riyal',
  BDT: 'Bangladeshi Taka',
  GHS: 'Ghanaian Cedi',
  BGN: 'Bulgarian Lev',
  VES: 'Sovereign Bolivar',
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  ADA: 'Cardano',
  BNB: 'BNB',
  DOT: 'Polkadot',
  XRP: 'XRP',
  LINK: 'Chainlink',
  LTC: 'Litecoin',
  XLM: 'Stellar',
  BCH: 'Bitcoin Cash',
  UNI: 'Uniswap',
  DOGE: 'Dogecoin',
  BITS: 'Bit',
  SATS: 'Satoshi',
  SLP: 'Smooth Love Potion',
  AXS: 'Axie Infinity',
  BHD: 'Bahraini Dinar',
  LKR: 'Sri Lankan Rupee',
  MMK: 'Myanmar Kyat',
  BMD: 'Bermudian Dollar',
  XDR: 'IMF Special Drawing Rights',
  KWD: 'Kuwaiti Dinar',
  VEF: 'Venezuelan bolívar',
};
